/** TanStack Query hooks for file upload CRUD */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient, API_BASE, ApiError } from '../lib/api-client'
import { appendUploadDescription } from '../lib/upload-description'

export interface Upload {
  id: string
  fileKey: string
  filename: string
  contentType: string
  sizeBytes: number
  extractionStatus: string | null // pending | processing | completed | failed | unsupported
  extractionMethod: string | null // vision | text | unsupported | null
  summary: string | null
  description: string | null
  lastSummaryAt: number | null // epoch ms (matches API serialization)
  createdAt: string
}

export function useUploads(enabled = true) {
  return useQuery<{ files: Upload[] }>({
    queryKey: ['uploads'],
    queryFn: () => apiClient.get<{ files: Upload[] }>('/api/uploads'),
    enabled,
    // Poll every 5s when any file is being processed
    refetchInterval: (query) => {
      const files = query.state.data?.files ?? []
      return files.some((f) => f.extractionStatus === 'processing') ? 5000 : false
    },
  })
}

export function useUploadFile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append('file', file)
      appendUploadDescription(formData, file, 'storage')
      const res = await fetch(`${API_BASE}/api/uploads`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      })
      if (!res.ok) {
        let data: unknown = null
        try {
          data = await res.json()
        } catch {
          data = null
        }
        const message =
          typeof data === 'object' && data !== null && 'error' in data
            ? String((data as Record<string, unknown>).error)
            : 'Upload failed'
        throw new ApiError(res.status, message, data)
      }
      return res.json() as Promise<{ id: string; fileKey: string; filename: string }>
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['uploads'] }),
  })
}

export function useDeleteUpload() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.delete<{ ok: boolean }>(`/api/uploads/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['uploads'] }),
  })
}

export function useRetryExtraction() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.post<{ ok: boolean }>(`/api/uploads/${id}/retry-extraction`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['uploads'] })
      qc.invalidateQueries({ queryKey: ['storage-files'] })
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : 'Retry failed'
      alert(`Extraction retry failed: ${msg}`)
    },
  })
}

/** PATCH /api/uploads/:id — update user description (optimistic) */
export function useUpdateUploadDescription() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, description }: { id: string; description: string | null }) =>
      apiClient.patch<{ ok: boolean }>(`/api/uploads/${id}`, { description }),
    onMutate: async ({ id, description }) => {
      await qc.cancelQueries({ queryKey: ['uploads'] })
      await qc.cancelQueries({ queryKey: ['storage-files'] })
      const prev = qc.getQueryData<{ files: Upload[] }>(['uploads'])
      qc.setQueryData<{ files: Upload[] }>(['uploads'], (old) =>
        old ? { files: old.files.map((f) => (f.id === id ? { ...f, description } : f)) } : old,
      )
      return { prev }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(['uploads'], ctx.prev)
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['uploads'] })
      qc.invalidateQueries({ queryKey: ['storage-files'] })
    },
  })
}

/** POST /api/uploads/:id/regenerate-summary — optimistically flip to processing */
export function useRegenerateSummary() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.post<{ ok: boolean }>(`/api/uploads/${id}/regenerate-summary`),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: ['uploads'] })
      await qc.cancelQueries({ queryKey: ['storage-files'] })
      const prev = qc.getQueryData<{ files: Upload[] }>(['uploads'])
      // Optimistic: stamp lastSummaryAt as epoch ms to match API serialization
      const nowMs = Date.now()
      qc.setQueryData<{ files: Upload[] }>(['uploads'], (old) =>
        old
          ? {
              files: old.files.map((f) =>
                f.id === id
                  ? { ...f, extractionStatus: 'processing', summary: null, lastSummaryAt: nowMs }
                  : f,
              ),
            }
          : old,
      )
      return { prev }
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.prev) qc.setQueryData(['uploads'], ctx.prev)
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['uploads'] })
      qc.invalidateQueries({ queryKey: ['storage-files'] })
    },
  })
}
